import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { Agent, AgentStatus, UpdateAgent } from '@orqforge/shared';
import {
  cursorCatalogModelId,
  isCursorModel,
  resolveCatalogModelParams,
  type CursorCatalogModel,
} from '@orqforge/shared';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/Dialog';
import { MarkdownPreview } from '../components/ui/MarkdownPreview';
import { AgentDetailTools } from './AgentDetailTools';
import { AgentDetailAccess } from './AgentDetailAccess';

const STATUSES: AgentStatus[] = ['draft', 'active', 'paused', 'archived'];

interface ChatLine {
  role: 'you' | 'agent';
  text: string;
}

function readApiError(err: unknown, fallback: string): string {
  if (!(err instanceof Error) || !err.message.trim()) return fallback;
  try {
    const body = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (Array.isArray(body.message) && body.message[0]) return body.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

function syncLabel(status: string): string {
  if (status === 'in_sync') return 'repo in sync';
  if (status === 'error') return 'sync error';
  return 'pending sync';
}

export function AgentDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [agent, setAgent] = useState<Agent | null>(null);
  const [models, setModels] = useState<CursorCatalogModel[]>([]);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cfgName, setCfgName] = useState('');
  const [cfgJob, setCfgJob] = useState('');
  const [cfgSystemPrompt, setCfgSystemPrompt] = useState('');
  const [cfgStatus, setCfgStatus] = useState<AgentStatus>('draft');
  const [cfgPii, setCfgPii] = useState(true);
  const [cfgInjection, setCfgInjection] = useState(true);
  const [cfgMaxToolCalls, setCfgMaxToolCalls] = useState(40);
  const [cfgPerRun, setCfgPerRun] = useState(0);
  const [cfgMonthly, setCfgMonthly] = useState(0);
  const [cfgModel, setCfgModel] = useState('composer-2.5');
  const [cfgLevelId, setCfgLevelId] = useState('');
  const [chat, setChat] = useState<ChatLine[]>([]);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [toolCount, setToolCount] = useState(0);
  const [integrationCount, setIntegrationCount] = useState(0);
  const [mcpCount, setMcpCount] = useState(0);
  const [skillCount, setSkillCount] = useState(0);
  const [knowledgeCount, setKnowledgeCount] = useState(0);

  const levels = useMemo(() => {
    const modelId = cursorCatalogModelId(cfgModel);
    return models.find((row) => row.id === modelId)?.levels ?? [];
  }, [cfgModel, models]);

  useEffect(() => {
    if (!id) return;
    void apiClient.getAgent(id).then(setAgent).catch(() => setAgent(null));
    void apiClient.listCursorModels().then((res) => setModels(res.cursor?.models ?? [])).catch((err) => {
      setCatalogError(readApiError(err, 'Could not load Cursor models'));
    });
    void apiClient.agentTools(id).then((rows) => setToolCount(rows.length)).catch(() => setToolCount(0));
    void apiClient.agentIntegrations(id).then((rows) => setIntegrationCount(rows.length)).catch(() => setIntegrationCount(0));
    void apiClient.agentMcpServers(id).then((rows) => setMcpCount(rows.length)).catch(() => setMcpCount(0));
    void apiClient.agentSkills(id).then((rows) => setSkillCount(rows.length)).catch(() => setSkillCount(0));
    void apiClient.agentKnowledgeBases(id).then((rows) => setKnowledgeCount(rows.length)).catch(() => setKnowledgeCount(0));
  }, [id]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat, sending]);

  function startConfigEdit() {
    if (!agent) return;
    const modelId = cursorCatalogModelId(agent.model.model) || 'composer-2.5';
    const modelLevels = models.find((row) => row.id === modelId)?.levels ?? [];
    setCfgName(agent.name);
    setCfgJob(agent.jobDescription);
    setCfgSystemPrompt(agent.systemPrompt);
    setCfgStatus(agent.status);
    setCfgPii(agent.guardrails.piiRedaction);
    setCfgInjection(agent.guardrails.promptInjectionDefense);
    setCfgMaxToolCalls(agent.guardrails.maxToolCallsPerRun);
    setCfgPerRun(agent.budget.perRunUsdCents);
    setCfgMonthly(agent.budget.monthlyUsdCents);
    setCfgModel(modelId);
    setCfgLevelId(agent.model.levelId ?? modelLevels[0]?.id ?? '');
    setConfigError(null);
    setConfigOpen(true);
  }

  async function saveConfig() {
    if (!id || configSaving) return;
    setConfigSaving(true);
    setConfigError(null);
    const modelId = cursorCatalogModelId(cfgModel) || cfgModel;
    const dto: UpdateAgent = {
      name: cfgName.trim(),
      jobDescription: cfgJob,
      systemPrompt: cfgSystemPrompt,
      status: cfgStatus,
      model: {
        model: `cursor:${modelId}`,
        levelId: cfgLevelId || undefined,
        params: resolveCatalogModelParams(models, modelId, cfgLevelId),
      },
      guardrails: {
        piiRedaction: cfgPii,
        promptInjectionDefense: cfgInjection,
        maxToolCallsPerRun: Number(cfgMaxToolCalls),
      },
      budget: {
        perRunUsdCents: Number(cfgPerRun),
        monthlyUsdCents: Number(cfgMonthly),
      },
    };
    try {
      const updated = await apiClient.updateAgent(id, dto);
      setAgent(updated);
      setConversationId(undefined);
      setConfigOpen(false);
    } catch (err) {
      setConfigError(readApiError(err, 'Could not update agent access.'));
    } finally {
      setConfigSaving(false);
    }
  }

  async function syncRepo() {
    if (!id || !agent || syncing) return;
    setSyncing(true);
    try {
      const sync = await apiClient.syncAgent(id);
      setAgent({ ...agent, sync });
    } catch {
      /* leave the current sync badge */
    } finally {
      setSyncing(false);
    }
  }

  async function remove() {
    if (!id || !agent || deleting) return;
    setDeleting(true);
    setAccessError(null);
    try {
      await apiClient.deleteAgent(id);
      navigate('/agents');
    } catch (err) {
      setAccessError(readApiError(err, 'Could not update agent access.'));
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function send() {
    const text = message.trim();
    if (!id || !text || sending) return;
    setChat((rows) => [...rows, { role: 'you', text }]);
    setMessage('');
    setSending(true);
    try {
      if (isCursorModel(agent?.model?.model)) {
        const res = await apiClient.sendChatMessage(id, text, conversationId);
        setConversationId(res.conversationId);
        const output = res.runId
          ? `Started Cursor Cloud Agent run ${res.runId} (${res.status}). Follow progress in Collaborate.`
          : `Message ${res.status}.`;
        setChat((rows) => [...rows, { role: 'agent', text: output }]);
      } else {
        const res = await apiClient.chat(id, text);
        setChat((rows) => [...rows, { role: 'agent', text: res.output }]);
      }
    } catch {
      setChat((rows) => [...rows, { role: 'agent', text: '[error contacting agent]' }]);
    } finally {
      setSending(false);
    }
  }

  if (!agent) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Agent</h1>
        <p className="mt-2 text-sm text-gray-500">Loading agent...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold leading-tight">{agent.name}</h1>
          <p className="text-sm text-gray-500">{agent.jobDescription || 'No description'}</p>
        </div>
        <Button variant="ghost" className="text-red-600" disabled={deleting} onClick={() => setConfirmDelete(true)}>
          {deleting ? 'Deleting...' : 'Delete agent'}
        </Button>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">Configuration</h2>
            <Button size="sm" variant="ghost" onClick={startConfigEdit}>Edit</Button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <Badge variant="secondary">{toolCount} tools</Badge>
            <Badge variant="secondary">{integrationCount} integrations</Badge>
            <Badge variant="secondary">{mcpCount} MCP servers</Badge>
            <Badge variant="secondary">{skillCount} skills</Badge>
            <Badge variant="secondary">{knowledgeCount} knowledge bases</Badge>
            <Badge variant="secondary">{agent.status}</Badge>
            <Badge variant="secondary">{agent.model.model}{agent.model.levelId ? ` · ${agent.model.levelId}` : ''}</Badge>
            {agent.sync && (
              <Badge variant={agent.sync.status === 'in_sync' ? 'success' : agent.sync.status === 'error' ? 'destructive' : 'warning'}>
                {syncLabel(agent.sync.status)}
              </Badge>
            )}
          </div>
          {agent.sync?.lastError && (
            <p className="mt-2 text-xs text-red-600">
              {agent.sync.lastError}
              {agent.sync.lastError.includes('GitHub') && (
                <> <Link to="/integrations" className="underline">Connect GitHub on Integrations</Link></>
              )}
            </p>
          )}
          {agent.systemPrompt && <p className="mt-2 line-clamp-3 text-xs text-gray-500">{agent.systemPrompt}</p>}
          <Button className="mt-3" size="sm" variant="ghost" disabled={syncing} onClick={() => void syncRepo()}>
            {syncing ? 'Syncing...' : 'Sync to repo'}
          </Button>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
          <h2 className="text-base font-semibold">Chat (Cursor Cloud Agent)</h2>
          <p className="mt-1 text-xs text-gray-500">
            Messages start a durable Collaborate run on the workspace GitHub repo. Follow-ups resume the same Cursor agent.
          </p>
          <div ref={scrollRef} className="mt-3 flex max-h-80 min-h-40 flex-col gap-2 overflow-auto">
            {chat.length === 0 && <p className="text-sm text-gray-400">Send a message to test this agent.</p>}
            {chat.map((line, index) => (
              <div key={index} className="flex flex-col">
                <span className="mb-0.5 px-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
                  {line.role === 'you' ? 'You' : agent.name}
                </span>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${line.role === 'you' ? 'bg-gray-100 dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/60'}`}>
                  <MarkdownPreview content={line.text} variant="chat" />
                </div>
              </div>
            ))}
            {sending && <div className="px-1 text-xs text-gray-400">thinking...</div>}
          </div>
          <div className="mt-3 flex gap-2">
            <Input
              placeholder="Message the agent..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyUp={(e) => { if (e.key === 'Enter') void send(); }}
            />
            <Button size="sm" disabled={sending} onClick={() => void send()}>Send</Button>
          </div>
        </section>
      </div>

      {accessError && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
          {accessError}
        </div>
      )}

      <AgentDetailTools agentId={id} onError={setAccessError} />
      <AgentDetailAccess agentId={id} onError={setAccessError} />

      <Dialog open={configOpen} onOpenChange={(open) => { if (!open) setConfigOpen(false); }}>
        <DialogContent maxWidth="lg">
          <DialogHeader><DialogTitle>Edit agent</DialogTitle></DialogHeader>
          <DialogBody>
            <div className="grid gap-3">
              <label className="text-xs text-gray-500">Name<Input className="mt-1" value={cfgName} onChange={(e) => setCfgName(e.target.value)} /></label>
              <label className="text-xs text-gray-500">
                Job description
                <textarea className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" rows={2} value={cfgJob} onChange={(e) => setCfgJob(e.target.value)} />
              </label>
              <label className="text-xs text-gray-500">
                Standing instructions
                <textarea className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" rows={4} value={cfgSystemPrompt} onChange={(e) => setCfgSystemPrompt(e.target.value)} />
              </label>
              <div>
                <span className="mb-1.5 block text-xs text-gray-500">Status</span>
                <div className="inline-flex flex-wrap gap-1 rounded-xl border border-gray-200 p-1 dark:border-gray-700">
                  {STATUSES.map((status) => (
                    <button key={status} type="button" className={`rounded-lg px-3 py-1.5 text-sm ${cfgStatus === status ? 'bg-blue-600 text-white' : ''}`} onClick={() => setCfgStatus(status)}>
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              <label className="text-xs text-gray-500">
                Cursor model
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                  value={cfgModel}
                  onChange={(e) => {
                    const next = e.target.value;
                    setCfgModel(next);
                    const nextLevels = models.find((row) => row.id === cursorCatalogModelId(next))?.levels ?? [];
                    setCfgLevelId(nextLevels[0]?.id ?? '');
                  }}
                >
                  {models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                  {models.length === 0 && <option value={cfgModel}>{cfgModel}</option>}
                </select>
              </label>
              {levels.length > 0 && (
                <label className="text-xs text-gray-500">
                  Level
                  <select className="mt-1 w-full rounded-md border border-gray-300 bg-white px-2 py-2 text-sm dark:border-gray-600 dark:bg-gray-800" value={cfgLevelId} onChange={(e) => setCfgLevelId(e.target.value)}>
                    {levels.map((level) => <option key={level.id} value={level.id}>{level.label}</option>)}
                  </select>
                </label>
              )}
              <p className="text-xs text-gray-500">
                Collaborate runs as a Cursor Cloud Agent on the workspace GitHub repository. Changing model or level starts a new Cloud Agent.
              </p>
              {catalogError && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">{catalogError}</div>}
              <div className="text-xs text-gray-400">Guardrails</div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfgPii} onChange={(e) => setCfgPii(e.target.checked)} /> PII redaction</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cfgInjection} onChange={(e) => setCfgInjection(e.target.checked)} /> Prompt injection defense</label>
              <label className="text-xs text-gray-500">
                Max tool calls per run
                <Input className="mt-1" type="number" min={1} max={200} value={cfgMaxToolCalls} onChange={(e) => setCfgMaxToolCalls(Number(e.target.value))} />
              </label>
              <div className="text-xs text-gray-400">Budget</div>
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs text-gray-500">Per-run cap (USD cents)<Input className="mt-1" type="number" min={0} value={cfgPerRun} onChange={(e) => setCfgPerRun(Number(e.target.value))} /></label>
                <label className="text-xs text-gray-500">Monthly cap (USD cents)<Input className="mt-1" type="number" min={0} value={cfgMonthly} onChange={(e) => setCfgMonthly(Number(e.target.value))} /></label>
              </div>
              {configError && <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">{configError}</div>}
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setConfigOpen(false)}>Cancel</Button>
            <Button size="sm" disabled={configSaving} onClick={() => void saveConfig()}>{configSaving ? 'Saving...' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(false); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete agent</DialogTitle></DialogHeader>
          <DialogBody><p className="text-sm">Delete agent "{agent.name}"? This cannot be undone.</p></DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
            <Button variant="destructive" disabled={deleting} onClick={() => void remove()}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
